import { InflateRequestMode } from '@sccs/types';
import { intersect, pipe, rawTransform } from 'valibot';
import { processDlsConsumption } from '../../technicalData/dls/consumptions';
import { ParsedTechnicalData, technicalDataParserDls } from '../../technicalData/dls/TechnicalDataDls';
import { MixedDlsTeaserResponse } from '../../types/dls/dls-response/ProductDto';
import { appendUserContextToUrl } from '../../util/EnhanceUrlWithCtx';
import { generatePrices } from '../helper/prices';
import {
  Availability,
  Characteristics,
  CharacteristicsVehicleTileData,
  Common,
  Consumptions,
  Cta,
  Description,
  EcoRatingVehicleTileData,
  Heading,
  Highlight,
  Highlights,
  ImageVehicleTileData,
  MatchRate,
  OnlineCode,
  Prices,
  SchemaAny,
  Tag,
} from '../VehicleTileDataSchema';

export const DlsTeaserCommonTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Common>(({ dataset, addIssue, NEVER }) => {
    if (dataset.value === null) {
      addIssue({
        message: 'The Dataset in DlsTeaserCommonTransformer is null ',
        path: [{ type: 'object', origin: 'value', input: dataset.value, key: 'value', value: dataset.value }],
      });
      return NEVER;
    }

    return {
      common: {
        hasCriticalError: false,
        brand: dataset.value?.brand,
        productType: 'STOCK',
        bm6: dataset.value?.baumuster,
        modelIdentifier: dataset.value?.modelIdentifier?.code,
        vehicleName: dataset.value?.name,
        identification: {
          commissionNumber: dataset.value?.commissionNumber,
          productCode: dataset.value?.code ?? 'no code',
          configurationId: dataset.value?.configurationId,
        },
        isVan:
          dataset.value?.dcpProductType?.name === 'VAN_COMMERCIAL' ||
          dataset.value?.dcpProductType?.name === 'VAN_PRIVATE',
      },
    };
  }),
);

export const DlsTeaserTagTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Tag>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'provided data is null',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const badgesArray =
      dataset.value.badges
        ?.filter((badge) => !!badge?.name)
        .map((badge) => ({
          name: badge?.name ?? '',
          description: badge?.description ?? undefined,
          footnote: badge?.footnote ?? undefined,
        })) ?? [];

    return {
      tag: {
        badges: [
          {
            showIcon: true,
            badges: badgesArray,
          },
        ],
      },
    };
  }),
);

export const DlsTeaserImageTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, ImageVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'provided data is null',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const imageUrl = dataset.value.primaryImage?.url;
    if (!imageUrl) {
      addIssue({
        message: 'Image URL is not defined',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
    }

    return {
      image: {
        url: imageUrl,
        alt: undefined,
        title: undefined,
      },
    };
  }),
);

export const DlsTeaserHeadingTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Heading>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for ProductDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const headingContent = dataset.value.name;
    if (!headingContent) {
      addIssue({
        message: 'The Heading is not defined',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
    }

    return {
      heading: {
        content: headingContent,
      },
    };
  }),
);

export const DlsTechnicalDataTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Partial<ParsedTechnicalData>>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in DlsTechnicalDataTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const { lang, market, translations, mode, ...rest } = dataset.value;
    try {
      const parser = technicalDataParserDls(rest, market, lang, mode, translations);
      const parsedTechnicalData = parser.getTechnicalData();
      return parsedTechnicalData;
    } catch (error) {
      addIssue({
        message: 'technicalData Parsing failed',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as unknown as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      throw error;
    }
  }),
);

export const DlsTeaserCharacteristicsTransformer = pipe(
  SchemaAny,
  DlsTechnicalDataTransformer,
  rawTransform<Partial<ParsedTechnicalData>, CharacteristicsVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in ProductDtoCharacteristicsTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    if (!dataset.value) {
      return {
        characteristics: [],
      };
    }
    const parsedCharacteristics: Characteristics[] = [
      dataset.value.engineDescription,
      dataset.value.fuelType,
      dataset.value.gearBox,
      dataset.value.finalInspectionDate,
    ].filter(Boolean) as Characteristics[];
    return {
      characteristics: parsedCharacteristics,
    };
  }),
);

export const DlsTeaserPricesTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Prices>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for DlsTeaserPricesTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const priceCode = dataset.value?.code;
    const dcpProductTypeName = dataset.value?.dcpProductType?.name;
    if (!priceCode) {
      addIssue({
        message: 'code is undefined, but is important for the prices',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    if (!dcpProductTypeName) return NEVER;
    return {
      prices: generatePrices({
        productCode: priceCode,
        commissionNumber: dataset.value.commissionNumber,
        configurationId: dataset.value.configurationId,
        dcpProductType: dcpProductTypeName,
        market: dataset.value.market,
        locale: dataset.value.lang,
        currency: dataset.value.price?.currencyIso,
        priceRawValue: dataset.value.price?.value,
      }),
    };
  }),
);

export const DlsTeaserHighlightTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Highlights>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for ProductDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    const highlights: Highlight[] = [dataset.value?.highlight1, dataset.value?.highlight2, dataset.value?.highlight3]
      .filter(Boolean)
      .map((highlight) => ({ content: highlight }));

    return {
      highlights: highlights,
    };
  }),
);

export const DlsTeaserConsumptionTransformer = pipe(
  SchemaAny,
  DlsTechnicalDataTransformer,
  rawTransform<Partial<ParsedTechnicalData>, Consumptions>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in ProductDtoConsumptionTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      consumption: processDlsConsumption(dataset.value),
    };
  }),
);

export const DlsTeaserEcoRatingTransformer = pipe(
  SchemaAny,
  DlsTechnicalDataTransformer,
  rawTransform<Partial<ParsedTechnicalData>, EcoRatingVehicleTileData>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'dataset has some issues, located in DlsTeaserEcoRatingTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      ecoRating: {
        image: dataset.value.ecoRating?.image,
        emission: dataset.value.ecoRating?.emission,
      },
    };
  }),
);

export const DlsTeaserCtaTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Cta>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for ProductDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    const pdpLink = dataset.value?.productUrl;
    const srpLink = dataset.value?.srpUrl;
    if (
      dataset.value.mode === InflateRequestMode.SIMPLE_PRICE ||
      dataset.value.mode === InflateRequestMode.BANNER_CAMPAIGN
    ) {
      if (!srpLink) {
        addIssue({
          message: 'The SRP Link is not provided, but is important for the Teaser',
          path: [
            {
              type: 'object',
              origin: 'value',
              input: dataset.value as Record<string, unknown>,
              key: 'value',
              value: dataset.value,
            },
          ],
        });
        return NEVER;
      }
    } else {
      if (!pdpLink) {
        addIssue({
          message: 'The PDP Link is not provided, but is important for the Teaser',
          path: [
            {
              type: 'object',
              origin: 'value',
              input: dataset.value as Record<string, unknown>,
              key: 'value',
              value: dataset.value,
            },
          ],
        });
        return NEVER;
      }
    }

    return {
      cta: {
        pdp: appendUserContextToUrl(pdpLink, dataset.value.ctx),
        srp: appendUserContextToUrl(srpLink, dataset.value.ctx, dataset.value.sortType),
      },
    };
  }),
);

export const DlsTeaserMatchRateTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, MatchRate>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in DlsTeaserMatchRateTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      rate: undefined,
    };
  }),
);

export const DlsTeaserOnlineCodeTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, OnlineCode>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in ProductDtoOnlineCodeTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      content: undefined,
    };
  }),
);

export const DlsTeaserAvailabilityTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Availability>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'issues in DlsTeaserAvailabilityTransformer',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'results',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }
    return {
      status: undefined,
      estimatedArrivalDate: undefined,
    };
  }),
);

export const DlsTeaserDescriptionTransformer = pipe(
  SchemaAny,
  rawTransform<MixedDlsTeaserResponse, Description>(({ dataset, addIssue, NEVER }) => {
    if (!dataset || !dataset.value) {
      addIssue({
        message: 'Dataset or dataset.value is null for ProductDto',
        path: [
          {
            type: 'object',
            origin: 'value',
            input: dataset.value as Record<string, unknown>,
            key: 'value',
            value: dataset.value,
          },
        ],
      });
      return NEVER;
    }

    return {
      description: {
        content: undefined,
      },
    };
  }),
);

export const ConvertDlsTeaserToVehicleDataTransformer = intersect([
  DlsTeaserCommonTransformer,
  DlsTeaserTagTransformer,
  DlsTeaserImageTransformer,
  DlsTeaserHeadingTransformer,
  DlsTeaserCharacteristicsTransformer,
  DlsTeaserPricesTransformer,
  DlsTeaserHighlightTransformer,
  DlsTeaserConsumptionTransformer,
  DlsTeaserEcoRatingTransformer,
  DlsTeaserCtaTransformer,
]);
